import { Component, OnInit, Input, ViewChild, AfterViewInit, Renderer2, Output, EventEmitter, OnDestroy } from '@angular/core';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ScoutPath, ScoutPoint, MapDevice } from '../../../core/models';
import { ScoutPathPlotterComponent } from '../../scout-path/scout-path-plotter/scout-path-plotter.component';
import { timer, Subscription, BehaviorSubject } from 'rxjs';
import { ScoutTaskDoc } from '../../../core/schema/scout-task-schema';
import { ScoutPointPaginatorComponent } from '../scout-point-paginator/scout-point-paginator.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ScoutPointEnvironmentComponent } from '../scout-point-environment/scout-point-environment.component';
import { ScoutPointEditorComponent } from '../scout-point-editor/scout-point-editor.component';
import { ScoutPointService, ScoutTaskService, AntennaService } from '../../../core/services';
import { ScoutPointRefMeasurementsComponent } from '../scout-point-ref-measurements/scout-point-ref-measurements.component';
import { ScoutPointPhotoDoc } from '../../../core/schema/scout-point-photo-schema';
import { skip, skipWhile, take } from 'rxjs/operators';
import { ScoutPointErrorCalcComponent } from '../scout-point-error-calc/scout-point-error-calc.component';
import { NgForm } from '@angular/forms';
import { AppState, RoleMgr } from '../../../core';
import { ScoutPointPhotoBrowserComponent, ScoutPointPhotoGroupSelected, ScoutPointPhotoSelected } from '../scout-point-photo-browser/scout-point-photo-browser.component';
import { ScoutPointPhotoModal } from '../scout-point-photo-modal/scout-point-photo-modal.component';
import { PointDoc } from '../../../core/schema/point-schema';


@Component({
  selector: 'modal-content',
  templateUrl: './scout-point-ref-dialog.component.html',
  styleUrls: ['./scout-point-ref-dialog.component.scss'],
  providers: [ ScoutPathPlotterComponent ]
})
export class ScoutPointRefDialogComponent implements OnInit, OnDestroy, AfterViewInit {

  static readonly ModalId = 1099

  @Input() scoutPathId: string
  @Input() scoutPointId: string
  @Input() scoutTaskDoc : ScoutTaskDoc
  @Input() buildingPointsSubject : BehaviorSubject<PointDoc[]>
  @Input() allowEditMode ?: boolean = true
  @Input() locked : boolean
  @Input() hidePhotos? : boolean = false

  @Output() onClosed = new EventEmitter<void>() //TODO - remove when ngx-bootstrap release branch has BsModalRef.onHide (in development branch)

  @ViewChild('scoutPathPlotter') scoutPathPlotter : ScoutPathPlotterComponent
  @ViewChild('scoutPointPaginator') scoutPointPaginator : ScoutPointPaginatorComponent
  @ViewChild('scoutPointRefMeasurements') scoutPointRefMeasurements : ScoutPointRefMeasurementsComponent
  @ViewChild('scoutPointEnvironment') scoutPointEnvironment : ScoutPointEnvironmentComponent
  @ViewChild('errorCalcComponent') errorCalcComponent : ScoutPointErrorCalcComponent
  @ViewChild('scoutPointEditor') scoutPointEditor: ScoutPointEditorComponent;
  @ViewChild('photoBrowser') photoBrowser: ScoutPointPhotoBrowserComponent;

  scoutPoint : ScoutPoint
  scoutPath : ScoutPath
  isEditing = false
  bIsModified = false
  bIsValid = true
  bReady = false // modal needs to render first otherwise plotter gets confused about its size
  errorMsg : string
  bSavedChanges : boolean = false
  docChangeSubscription : Subscription
  isLoadingBuildingPoints : boolean = false
  buildingPoints : PointDoc[] = []
  buildingPointsSubscription : Subscription
  antennaDevicesSubscription: Subscription
  devices: MapDevice[] = []
  bearingDefaultAngles : number[]
  scoutPathPlotterCssClass : string = 'medium-plotter'

  constructor(public bsModalRef: BsModalRef, private renderer: Renderer2, private modalService: BsModalService,
    public roleMgr : RoleMgr,
    protected appState : AppState,
    protected scoutTaskService : ScoutTaskService,
    protected scoutPointService : ScoutPointService,
    protected antennaService : AntennaService ) {
  }

  ngOnInit() {
    const plotterCssClass = this.appState.settings.getScoutRefDlgPlotterCssClass()
    if ( plotterCssClass ) {
      this.scoutPathPlotterCssClass = plotterCssClass
    }
    this.initUsingScoutTaskDoc()
    this.docChangeSubscription = this.scoutTaskDoc.$.pipe( skip(1) ).subscribe( doc => {this.docChanged(doc); this.bReady = true })
    if ( this.buildingPointsSubject == null ) {
      this.buildingPointsSubject = new BehaviorSubject<PointDoc[]>(null)
      this.scoutTaskService.loadHaPointsInPath(this.scoutPath).then( pts => this.buildingPointsSubject.next(pts) )
    }
    this.isLoadingBuildingPoints = this.buildingPointsSubject.value == null
    this.buildingPoints = this.buildingPointsSubject.value == null ? [] : this.buildingPointsSubject.value
    this.buildingPointsSubscription = this.buildingPointsSubject.subscribe( points => {
      this.isLoadingBuildingPoints = points == null
      this.buildingPoints = this.isLoadingBuildingPoints ? [] : points
    })
    this.subscribeToAntennasForBuildingStageFloor()
  }


  ngAfterViewInit() {
    this.bReady = true // okay for plotter to be added to page now
    //this.initialize()
    timer(0).subscribe(ms=> this.updateControls() )
  }


  ngOnDestroy() {
    this.docChangeSubscription?.unsubscribe()
    this.buildingPointsSubscription?.unsubscribe()
    this.antennaDevicesSubscription?.unsubscribe()
    this.onClosed.emit()
  }


  async askDiscardEdits(acceptDiscardFn: () => void) {
    let initialState = {
      onYes: ()=>{
        timer(100).subscribe( _ => acceptDiscardFn() )
      },
      question: 'Discard scout point reference changes for ' + this.scoutPoint?.name + '?',
      focusOnNo: true,
      yesStyle: 'btn btn-danger'
    }
    this.modalService.config.animated = false
    this.modalService.show(ConfirmModalComponent, { initialState });
  }


  cancelEdit = () => {
    //console.log('cancelEdit()')
    if ( this.scoutPointRefMeasurements ) {
      if ( this.scoutPointRefMeasurements.selectedPhoto?.value ) {
        this.scoutPointRefMeasurements.selectedPhoto.next(null)
      }
      this.scoutPointRefMeasurements.photoEditors?.forEach(photoEditor=>{
        photoEditor.reset()
      })
    }
    const tmpPt = this.scoutPoint
    if ( !tmpPt || tmpPt.bNew ) {
      this.initUsingScoutTaskDoc()
      this.setSelectedPointById(tmpPt?.parentId)
      this.updateControls()
    }
    this.initialize();
    this.scoutPathPlotter?.plotScoutPath(this.scoutPath)
    this.isEditing = false
  }


  changeSelectedPointGuard = ( current: ScoutPoint, acceptFn: () => void ) => {
    if ( !this.isModified() ) {
      acceptFn()
    } else {
      this.askDiscardEdits(()=>{
        this.cancelEdit();
        acceptFn()
      })
    }
  }

  externalEditsDlgOpen = false
  docChanged( changeEvent : any ) {
    if ( this.bIsSaving) {
      return
    }
    console.log('docChanged()')
    if ( this.isEditing && this.bIsModified ) {
      console.warn('Scout document changed but edits are in progress')
      const newScoutPath = this.scoutTaskDoc.scoutPaths.find(path=>path.id==this.scoutPathId)
      this.scoutTaskService.updateScoutPathContext(newScoutPath,this.buildingPoints)
      const newScoutPoint = newScoutPath.scoutPoints.find(point=>point.id==this.scoutPointId)
      if ( this.scoutPointService.pointDiffers(this.scoutPoint,newScoutPoint,true) ) {
        let initialState = {
          onYes: () => {
            this.externalEditsDlgOpen = false
            this.cancelEdit()
          },
          onNo: () => {
            this.externalEditsDlgOpen = false
          },
          question: 'Scout point changed outside editor.  Would you like to accept the external changes and lose your current edits?',
          focusOnNo: true,
          yesStyle: 'btn btn-danger'
        }
        this.externalEditsDlgOpen = true
        this.modalService.show(ConfirmModalComponent, { initialState,  id: 1041 });
      }
    } else {
      this.initUsingScoutTaskDoc()
      this.updateControls()
    }
  }


  getNgForms() {
    let forms = []
    if ( this.scoutPointEditor?.scoutPointForm ) {
      forms.push(this.scoutPointEditor.scoutPointForm)
    }
    if ( this.scoutPointEnvironment?.scoutPointEnvForm ) {
      forms.push(this.scoutPointEnvironment.scoutPointEnvForm)
    }
    if ( this.errorCalcComponent?.errorCalcForm ) {
      forms.push(this.errorCalcComponent.errorCalcForm)
    }
    if ( this.scoutPointRefMeasurements?.refMeasurementsForm ) {
      forms.push(this.scoutPointRefMeasurements.refMeasurementsForm)
    }
    const photoEditorForms = this.scoutPointRefMeasurements?.photoEditors?.map(photoEditor=>photoEditor.photoForm)
    if ( photoEditorForms ) {
      forms.push(...photoEditorForms)
    }
    return forms
  }


  initialize() {
    //console.log('initialize()')
    this.errorMsg = ''
    this.scoutTaskService.updateScoutPathContext(this.scoutPath,this.buildingPoints)
    this.selectedScoutPointById(this.scoutPointId);
    this.getNgForms().forEach( ngForm => ngForm && ngForm.form.markAsPristine() )
    this.updateState()
  }

  selectedScoutPointById( scoutPtId: string ) {
    this.scoutPoint = this.scoutPath.scoutPoints.find(point=>point.id==scoutPtId)
    if (this.scoutPoint.disabled === undefined) {
      this.scoutPoint.disabled = false;
    }
  }

  initUsingScoutTaskDoc() {
    console.log('initUsingScoutTaskDoc() ' + this.scoutPoint?.name)
    this.scoutPath = this.scoutTaskDoc.scoutPaths.find(path=>path.id==this.scoutPathId)
    this.bearingDefaultAngles = this.scoutTaskService.getDefaultBearingAngles(this.scoutPath)
    this.scoutTaskService.updateScoutPathContext(this.scoutPath,this.buildingPoints)
    this.selectedScoutPointById(this.scoutPointId);
    this.subscribeToAntennasForBuildingStageFloor()
  }


  isModified() {
    const bModified = this.getNgForms().some( form => form && form.dirty )
    //console.log('isModified() ' + bModified)
    return bModified
  }


  isValid() {
    const bValid = this.getNgForms().every( form => !form || form.valid)
    //console.log('isValid() ' + bValid)
    return bValid
  }


  onCancel() {
    if ( !this.isModified() ) {
      this.cancelEdit()
    } else {
      this.askDiscardEdits(this.cancelEdit)
    }
  }


  onCloseModal = () => {
    const closeFn = () => {
      this.modalService.hide(ScoutPointRefDialogComponent.ModalId)
    }
    if ( !this.isModified() ) {
      closeFn()
    } else {
      this.askDiscardEdits(closeFn)
    }
  }


  async onScoutPointDisabled(disabled: boolean) {
    // save right away (not tied to form save button)
    console.log(`onScoutPointDisabled(${disabled})`,this.scoutPoint);
    await this.scoutTaskService.updateScoutPoint({
      scoutTaskDoc: this.scoutTaskDoc,
      scoutPathId: this.scoutPath.id,
      scoutPointId: this.scoutPoint.id,
      changes: { disabled },
      buildingPoints: this.buildingPoints
    });
  }

  delete = async ( scoutPoint : ScoutPoint ) => {
    const bHasChildren = this.scoutPath.scoutPoints.some(sp=>sp.parentId == scoutPoint.id)
    try {
      await this.scoutTaskService.deleteScoutPoint( this.scoutTaskDoc, this.scoutPath, scoutPoint )
      if ( bHasChildren ) {
        this.scoutTaskService.publishInfo('Renaming children of old ' +scoutPoint.name+ ' (name reused)', this, 30 )
      }
    } catch ( error ) {
      this.scoutTaskService.publishError('Failed removing all scout point data for ' + scoutPoint?.name, this )
    }
    //this.initUsingScoutTaskDoc()
    if ( this.scoutPath?.scoutPoints.findIndex(sp=>sp.id==scoutPoint.id) == -1 ) {
      // must have been deleted so set select/focus on parent
      this.setSelectedPointById( scoutPoint.parentId )
    }
  }


  onDelete = ( scoutPoint : ScoutPoint ) => {
    if ( !scoutPoint ) {
      scoutPoint = this.scoutPoint
    }
    let initialState = {
      onYes: ()=>{this.delete(scoutPoint)},
      question: 'Delete ' + scoutPoint?.name + ' and all photos from database?',
      focusOnNo: true,
      yesStyle: 'btn btn-danger'
    }
    this.modalService.config.animated = false
    this.modalService.show(ConfirmModalComponent, { initialState });
  }


  onEdit() {
    this.initialize()
    this.isEditing = true
  }


  onPointEditorEditModeChanged( editor : ScoutPointEditorComponent ) {
    if ( !this.isEditing && editor.isEditing ) {
      this.onEdit()
    }
  }


  onEnvEditorEditModeChanged( editor : ScoutPointEnvironmentComponent ) {
    if ( !this.isEditing && editor.isEditing ) {
      this.onEdit()
    }
  }


  onMeasurementsEditModeChanged( editor : ScoutPointRefMeasurementsComponent ) {
    if ( !this.isEditing && editor.isEditing ) {
      this.onEdit()
    }
  }


  onErrorCalcEditModeChanged( editor : ScoutPointErrorCalcComponent ) {
    if ( !this.isEditing && editor.isEditing ) {
      this.onEdit()
    }
  }


  onErrorCalcChanged( form : NgForm ) {
    //recompute the error estimates for all points (cumulative estimates depend on all children)
    this.scoutTaskService.updateScoutPathContext(this.scoutPath,this.buildingPoints)
    this.updateState()
  }


  bResizeMap = false
  onResizeMap( cssClass : string ) {
    this.scoutPathPlotterCssClass=cssClass
    try{
      this.appState.settings.setScoutRefDlgPlotterCssClass(cssClass)
    } catch ( err ) {
      console.error(err)
    }
    this.bResizeMap = true
    timer(0).subscribe(_=>{
      this.bResizeMap=false
      timer(0).subscribe(_=> this.plotWhenMapReady() )
    })
  }

  plotWhenMapReady() {
    this.scoutPathPlotter.mapReady.pipe( skipWhile(bReady=>!bReady), take(1) ).subscribe({
      next: (bReady:boolean) => {
        this.scoutPathPlotter.plotScoutPath(this.scoutPath)
        this.scoutPathPlotter.setSelectedPoint(this.scoutPoint)
      },
      error: (err:any) => {
        console.error(err)
        this.scoutTaskService.publishError(err,this)
      }
    })
  }

  bIsSaving = false
  async onSave() {
    try {
      this.bIsSaving = true
      await this.scoutPointRefMeasurements?.savePhotoEdits()
      if ( this.scoutPointEditor?.scoutPointForm?.dirty
          || this.scoutPointEnvironment?.scoutPointEnvForm?.dirty
          || this.scoutPointRefMeasurements?.refMeasurementsForm.dirty
          || this.errorCalcComponent?.errorCalcForm.dirty ) {
        if ( this.scoutPointEditor?.scoutPointForm?.dirty ) {
          // changes in lat, lng, bearing, etc may impact children so save entire path
          this.scoutTaskService.updateScoutPathContext(this.scoutPath,this.buildingPoints)
          await this.scoutTaskService.saveScoutPath(this.scoutTaskDoc,this.scoutPath,this.buildingPoints)
        } else {
          await this.scoutTaskService.saveScoutPoint(this.scoutTaskDoc,this.scoutPath,this.scoutPoint,this.buildingPoints)
        }
      }
      if ( this.scoutPoint.bNew ) {
        this.scoutPoint.bNew = false
      }
      this.bSavedChanges = true // when dlg closes, segments editor will reload some or all points if 'true'
      this.cancelEdit()
    } catch ( error ) {
      this.errorMsg = this.scoutTaskService.extractErrorMsg(error)
    }
    this.bIsSaving = false
  }


  onScoutPointModified( scoutPoint : ScoutPoint ) {
    console.log('onScoutPointModified() ' + scoutPoint?.name )
    if ( !this.bIsSaving ) {
      this.scoutTaskService.updateScoutPathContext(this.scoutPath,this.buildingPoints)
      this.scoutPathPlotter?.plotScoutPath(this.scoutPath)
      this.updateState()
    }
  }


  onPhotoAdded( photoDoc : ScoutPointPhotoDoc ) {
    console.log('photoAdded()')
    const editor = this.scoutPointRefMeasurements.photoEditors.find(editor=>editor.photoDoc == photoDoc)
    this.photoBrowser.addPhoto(photoDoc);
    if ( editor?.photoForm ) {
      editor.photoForm.form.markAsDirty()
      this.updateState()
    }
  }


  onPhotoGroupSelected( event : ScoutPointPhotoGroupSelected ) {
    if ( !this.hidePhotos ) {
      this.scoutPointRefMeasurements.scrollTo(event.group.name.replace(/^point-to-point/,'path-view') + '-photos')
    }
  }


  onPhotoSelected( event : ScoutPointPhotoSelected ) {
    if ( !this.hidePhotos ) {
      const groupElementId = event.group.name.replace(/^point-to-point/,'path-view') + '-photos'
      const elementId = groupElementId + '-' + event.photos.findIndex(p=>p==event.photo)
      this.scoutPointRefMeasurements.scrollTo(elementId)
    } else {
      const context : ModalOptions = {
        initialState: {
          locked: this.locked,
          ...event
        },
        class: 'photo-modal',
        id: ScoutPointPhotoModal.ModalId,
        animated: true,
        ignoreBackdropClick: true
      }
      const modalRef = this.modalService.show(ScoutPointPhotoModal, context );
    }
  }

  @Output() selectedPointChanged = new EventEmitter<ScoutPoint>()

  setSelectedPoint( scoutPoint : ScoutPoint ) {
    console.log( 'setSelectedPoint(' + scoutPoint?.name +')')
    if ( scoutPoint && scoutPoint != this.scoutPoint ) {
      this.selectedPointChanged.emit(scoutPoint)
      this.scoutPointId = scoutPoint.id
      this.scoutPoint = this.scoutPath.scoutPoints.find(point=>point.id==scoutPoint.id)
      this.initialize()
      this.updateControls()
    }
  }


  setSelectedPointById( id : string ) {
    console.log('setSelectedPointById('+id+')')
    let scoutPoint = id ? this.scoutPath.scoutPoints.find(pt=>pt.id==id) : null
    if ( !scoutPoint ) {
      scoutPoint = this.scoutPath?.scoutPoints[0]
    }
    this.setSelectedPoint(scoutPoint)
  }


  updateControls() {
    this.scoutPointPaginator?.setSelected(this.scoutPoint)
    if ( this.scoutPointEditor ) {
      this.scoutPointEditor.updateScoutFromOptions()
      this.scoutPointEditor.setScoutPoint(this.scoutPoint)
    }
    if ( this.scoutPathPlotter ) {
      this.scoutPathPlotter.devices = this.devices
      this.plotWhenMapReady()
      //this.scoutPathPlotter.plot(this.scoutPath)
      //this.scoutPathPlotter.setSelectedPoint(this.scoutPoint)
    }
  }


  updateState() {
    this.bIsModified = this.isModified()
    //console.log('bIsModified: ' + this.bIsModified)
    this.bIsValid = this.isValid()
    if ( this.bIsModified && !this.isEditing ) {
      this.isEditing = true;
    }
  }

  private subscribeToAntennasForBuildingStageFloor() {
    // Unsubscribe from previous antenna subscription
    this.antennaDevicesSubscription?.unsubscribe()
    
    // Clear existing devices
    this.devices = []
    
    if (!this.scoutTaskDoc?.buildingId || !this.scoutPath?.floor) {
      console.log('[ScoutPointRefDialog] Missing buildingId or floor for antenna subscription')
      return
    }

    const params = {
      buildingId: this.scoutTaskDoc.buildingId,
      floorName: this.scoutPath.floor,
      stage: this.scoutTaskDoc.stage
    }

    try {
      const antennaObservable = this.antennaService.getAntennas$(params)
      
      this.antennaDevicesSubscription = antennaObservable.subscribe({
        next: (antennas) => {
          this.devices = antennas.map(antennaDoc => MapDevice.fromAntenna(antennaDoc))
          if (this.scoutPathPlotter) {
            this.scoutPathPlotter.devices = this.devices
          }
          console.log(`[ScoutPointRefDialog] Updated devices from antenna subscription: ${antennas.length} antennas for scout point`, params)
        },
        error: (e: any) => {
          const msg = `Failed to get antennas for scout point ${JSON.stringify(params)}: ${e.message}`
          console.error('[ScoutPointRefDialog] Antenna subscription error.', msg, e)
          this.antennaService.publishErrorObj(e, msg, this)
          this.devices = []
        }
      })
      
    } catch (e: any) {
      const msg = `Failed to subscribe to antennas for scout point ${JSON.stringify(params)}: ${e.message}`
      console.error('[ScoutPointRefDialog] Failed to create antenna subscription.', msg, e)
      this.antennaService.publishErrorObj(e, msg, this)
    }
  }

}